import React, { useEffect, useMemo } from "react"

import { useFormContext } from "react-hook-form"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element"
import { CheckboxElement } from "@front-shared/elements/checkbox.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { LookupService } from "@front-shared/services/lookup.service"

import { CHECKER_CONFIG } from "../../../../config/checker.config"
import { GENERAL_CONFIG } from "../../../../config/general.config"
import { LookupHelper } from "../../../../helpers/lookup.helper"
import { useIsFeatureFlagEnabled } from "../../../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { checkerSelector } from "../../../../store/selectors/checker.selector"
import type { IDynamicCheckerFormInputData } from "../../../../types/data.types"

export const PayerInput: React.FunctionComponent<
    IDynamicFormPayerInputProps
> = props => {
    const {
        formState: { errors },
        setValue,
        watch,
        register,
        unregister,
    } = useFormContext<IDynamicCheckerFormInputData>()

    const dataWatcher = watch()

    const { manualCheckType } = useAppSelector(checkerSelector)

    const payerNotAvailableEligible = useIsFeatureFlagEnabled([
        FeatureFlag.ONE_VERIFY_DISCOVER_MANUAL_CHECK,
        FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
    ])

    const medicaidPayerEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_MEDICAID_MANUAL_CHECK,
    )

    const supportedPayersUrl = useMemo<string | null>(() => {
        return manualCheckType
            ? CHECKER_CONFIG.supportedPayersUrl[manualCheckType]
            : null
    }, [manualCheckType])

    useEffect(() => {
        props.formDefaultValues?.payer &&
            setValue("payer", props.formDefaultValues?.payer)
    }, [])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <AutoCompleteElement
                name="payer"
                label="Payer"
                placeholder="Select one"
                disabled={props.disabled || dataWatcher.payerNotAvailable}
                searchOnFocus={props.searchOnFocus}
                dataFetcher={async (search?: string) => {
                    const medicaidDefaultPayer =
                        LookupHelper.getMedicaidDefaultPayer()

                    const items = await LookupService.lookupPayers(
                        search,
                        undefined,
                        undefined,
                        {
                            isAgenticPhoneSupported:
                                manualCheckType === QuotaUsageType.AGENTIC_PHONE
                                    ? true
                                    : undefined,
                            isAgenticPortalSupported:
                                manualCheckType ===
                                QuotaUsageType.AGENTIC_PORTAL
                                    ? true
                                    : undefined,
                            withFuzzyMatching: true,
                        },
                        {
                            weight: "DESC",
                            name: "ASC",
                        },
                    )

                    return [
                        ...(medicaidPayerEligible &&
                        search?.length &&
                        medicaidDefaultPayer.insuranceName
                            .toLowerCase()
                            .startsWith(search.toLowerCase())
                            ? [medicaidDefaultPayer]
                            : []),

                        ...items,
                    ]
                }}
                itemRenderer={LookupHelper.getRenderedPayer}
                reactHookFormErrors={errors}
                reactHookFormRegister={
                    dataWatcher.payerNotAvailable
                        ? undefined
                        : (register as any)
                }
                reactHookFormUnregister={unregister as any}
                reactHookFormSet={setValue as any}
                debounceMilliseconds={250}
                ignoreAutoSelectionOnBlur
                defaultValue={
                    props.formDefaultValues?.payer
                        ? LookupHelper.getRenderedPayer(
                              props.formDefaultValues.payer,
                          ).displayValue
                        : undefined
                }
                validations={
                    manualCheckType !== QuotaUsageType.MEDICAID
                        ? {
                              required: FRONT_VALIDATION_CONFIG.required,
                          }
                        : undefined
                }
                shouldValidate
                onSelected={props.onPayerSelected}
                notFoundTitle="Don’t see your payer?"
                notFoundSubtitle={`
                    <a
                        href="mailto:${GENERAL_CONFIG.supportEmail}?subject=Insurance provider not found"
                        target="_blank"
                        rel="noreferrer"
                    >
                        Contact Support
                    </a>
                `}
                isLabelStatic
            />

            {payerNotAvailableEligible &&
            !props.isAgenticCheck &&
            !props.disabled ? (
                <CheckboxElement
                    className={`
                        mt-[4px]
                        ${dataWatcher.payer ? "opacity-20" : ""}
                    `}
                    name="payerNotAvailable"
                    reactHookFormRegister={register as any}
                    reactHookFormUnregister={unregister as any}
                    reactHookFormSet={setValue as any}
                    watchDefaultCheckedChange
                    disabled={!!dataWatcher.payer}
                    defaultChecked={
                        typeof dataWatcher.payerNotAvailable !== "undefined"
                            ? dataWatcher.payerNotAvailable
                            : props.formDefaultValues?.payerNotAvailable
                    }
                    label={
                        <PrimaryText typography="caption">
                            Not available
                        </PrimaryText>
                    }
                />
            ) : (
                <div className="mb-[4px]" />
            )}

            {supportedPayersUrl ? (
                <div className="absolute left-[36px] top-[-22px]">
                    <a
                        href={supportedPayersUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="!no-underline"
                    >
                        <PrimaryText
                            typography="captionSemibold"
                            color={NIRVANA_COLORS.brand.vibrantPurple}
                        >
                            See Supported Payers
                        </PrimaryText>
                    </a>
                </div>
            ) : null}
        </div>
    )
}

interface IDynamicFormPayerInputProps {
    className?: string
    formDefaultValues?: Partial<IDynamicCheckerFormInputData>
    onPayerSelected: (payer: IPayer) => void
    isAgenticCheck?: boolean
    searchOnFocus?: boolean
    disabled?: boolean
}
